import $ from 'jquery'
import * as olSphere from 'ol/sphere'
import { Circle, Fill, Stroke, Style, Text } from 'ol/style'
import Draw from 'ol/interaction/Draw'
import LineString from 'ol/geom/LineString'
import Polygon from 'ol/geom/Polygon'
import Overlay from 'ol/Overlay'
import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
import { unByKey } from 'ol/Observable'
import * as olProj from 'ol/proj'
import { Coordinate } from 'ol/coordinate'
import MapBrowserEvent from 'ol/MapBrowserEvent'
/**
 * @module
 * @api
 */

import Tool from '../Tool'

/**
 * Message to show when the user is drawing a polygon.
 * @type {string}
 */
var continuePolygonMsg = 'Klicka för att fortsätta mäta med yta'

/**
 * Message to show when the user is drawing a line.
 * @type {string}
 */
var continueLineMsg = 'Klicka för att fortsätta mäta med linje'

export default class extends Tool {
  /**
   * @param {Object} config
   */
  constructor(config) {
    super(config)

    $.extend(this, config)

    this.source = new VectorSource()

    this.vector = new VectorLayer({
      source: this.source,
      style: feature => {
        return [
          new Style({
            fill: new Fill({
              color: 'rgba(255, 204, 51, 0.21)'
            }),
            stroke: new Stroke({
              color: 'rgba(255, 204, 51, 1)',
              width: 2
            }),
            image: new Circle({
              radius: 7,
              fill: new Fill({
                color: 'rgba(255, 204, 51, 0.21)'
              }),
              stroke: new Stroke({
                color: 'rgba(255, 204, 51, 1)',
                width: 2
              })
            }),
            text: new Text({
              font: 'normal 12px verdana',
              text: feature
                .get('measureOutput')
                .replace('<sup>', '')
                .replace('</sup>', ''),
              fill: new Fill({
                color: 'rgba(0, 0, 0, 1)'
              }),
              scale: 1,
              offsetY: -20
            })
          })
        ]
      }
    })

    if (this.geometryType === 'LineString') {
      this.title = 'Mät sträcka'
      this.description =
        'Klicka där du vill börja mäta\nDubbelklicka för att avslutat'
      this.iconCls = 'map-measure'
    } else {
      this.title = 'Mät yta'
      this.description =
        'Klicka där du vill börja mäta\nDubbelklicka för att avslutat'
      this.iconCls = 'map-measure-area'
    }

    this.ui.iconClass = this.iconCls

    this.measureTooltips = []
  }

  activate() {
    super.activate()

    this.createMeasureTooltip()
    this.createHelpTooltip()

    this.eventKey = this.map.on('pointermove', evt =>
      this.pointerMoveHandler(evt)
    )

    $(this.map.getViewport()).on('mouseout', () => {
      $(this.helpTooltipElement).addClass('hidden')
    })

    this.draw = new Draw({
      source: this.source,
      type: this.geometryType,
      style: new Style({
        fill: new Fill({
          color: 'rgba(255, 255, 255, 0.2)'
        }),
        stroke: new Stroke({
          color: 'rgba(255, 255, 0, 0.7)',
          lineDash: [10, 10],
          width: 2
        }),
        image: new Circle({
          radius: 5,
          stroke: new Stroke({
            color: 'rgba(0, 0, 0, 0.7)'
          }),
          fill: new Fill({
            color: 'rgba(255, 255, 255, 0.2)'
          })
        })
      })
    })

    var listener
    this.draw.on(
      'drawstart',
      function(evt) {
        this.sketch = evt.feature

        /** @type {Coordinate|undefined} */
        var tooltipCoord = evt.coordinate

        listener = this.sketch.getGeometry().on(
          'change',
          function(evt) {
            var geom = evt.target
            var output
            if (geom instanceof Polygon) {
              output = this.formatArea(/** @type {Polygon} */ geom)
              tooltipCoord = geom.getInteriorPoint().getCoordinates()
            } else if (geom instanceof LineString) {
              output = this.formatLength(/** @type {LineString} */ geom)
              tooltipCoord = geom.getLastCoordinate()
            }
            this.sketch.set('measureOutput', output)
            this.measureTooltipElement.innerHTML = output
            this.measureTooltip.setPosition(tooltipCoord)
          }.bind(this)
        )
      }.bind(this)
    )

    this.draw.on(
      'drawend',
      function() {
        this.measureTooltipElement.className =
          'measure-tooltip measure-tooltip measure-tooltip-static'
        this.measureTooltip.setOffset([0, -7])
        // unset sketch
        this.sketch = null
        // unset tooltip so that a new one can be created
        this.measureTooltipElement = null
        this.createMeasureTooltip()
        unByKey(listener)
      }.bind(this)
    )

    this.map.addLayer(this.vector)
    this.map.addInteraction(this.draw)
  }

  deactivate() {
    unByKey(this.eventKey)
    $(this.map.getViewport()).off()
    this.map.removeLayer(this.vector)
    this.map.removeInteraction(this.draw)
    this.helpTooltipElement.parentNode.removeChild(this.helpTooltipElement)
    delete this.helpTooltipElement
    this.measureTooltips.forEach(overlay => this.map.removeOverlay(overlay))
    this.measureTooltips = []
    this.source.clear()
    super.deactivate()
  }

  /**
   * Creates a new help tooltip
   */
  createHelpTooltip() {
    if (this.helpTooltipElement) {
      this.helpTooltipElement.parentNode.removeChild(this.helpTooltipElement)
    }
    this.helpTooltipElement = document.createElement('div')
    this.helpTooltipElement.className = 'measure-tooltip'
    this.helpTooltip = new Overlay({
      element: this.helpTooltipElement,
      offset: [15, 0],
      positioning: 'center-left'
    })
    this.map.addOverlay(this.helpTooltip)
  }

  /**
   * Creates a new measure tooltip
   */
  createMeasureTooltip() {
    if (this.measureTooltipElement) {
      this.measureTooltipElement.parentNode.removeChild(
        this.measureTooltipElement
      )
    }
    this.measureTooltipElement = document.createElement('div')
    this.measureTooltipElement.className =
      'measure-tooltip measure-tooltip-measure'
    this.measureTooltip = new Overlay({
      element: this.measureTooltipElement,
      offset: [0, -15],
      positioning: 'bottom-center'
    })
    this.map.addOverlay(this.measureTooltip)
    this.measureTooltips.push(this.measureTooltip)
  }

  /**
   * Handle pointer move.
   * @param {MapBrowserEvent} evt
   */
  pointerMoveHandler(evt) {
    if (evt.dragging) {
      return
    }
    /** @type {string} */
    var helpMsg = 'Klicka för att börja mäta'

    if (this.sketch) {
      var geom = this.sketch.getGeometry()
      if (geom instanceof Polygon) {
        helpMsg = continuePolygonMsg
      } else if (geom instanceof LineString) {
        helpMsg = continueLineMsg
      }
    }

    this.helpTooltipElement.innerHTML = helpMsg
    this.helpTooltip.setPosition(evt.coordinate)
    $(this.helpTooltipElement).removeClass('hidden')
  }

  /**
   * format length output
   * @param {LineString} line
   * @return {string}
   */
  formatLength(line) {
    var length
    // if (geodesicCheckbox.checked) {
    var coordinates = line.getCoordinates()
    length = 0
    var sourceProj = this.map.getView().getProjection()
    for (var i = 0, ii = coordinates.length - 1; i < ii; ++i) {
      var c1 = olProj.transform(coordinates[i], sourceProj, 'EPSG:4326')
      var c2 = olProj.transform(coordinates[i + 1], sourceProj, 'EPSG:4326')
      length += olSphere.getDistance(c1, c2, 6378137)
    }
    // } else {
    //  length = Math.round(line.getLength() * 100) / 100
    // }
    var output
    if (length > 1000) {
      output = Math.round((length / 1000) * 100) / 100 + ' ' + 'km'
    } else if (length > 100) {
      output = Math.round(length) + ' ' + 'm'
    } else {
      output = Math.round(length * 100) / 100 + ' ' + 'm'
    }
    return output
  }

  /**
   * format length output
   * @param {
   * Polygon} polygon
   * @return {string}
   */
  formatArea(polygon) {
    var area
    // if (geodesicCheckbox.checked) {
    var sourceProj = this.map.getView().getProjection()
    var geom = /** @type {Polygon} */ polygon
      .clone()
      .transform(sourceProj, 'EPSG:4326')
    var coordinates = geom.getLinearRing(0).getCoordinates()
    //area = Math.abs(wgs84Sphere.geodesicArea(coordinates))
    //Projection needed ?
    area = Math.abs(
      olSphere.getArea(geom, { projection: 'EPSG:4326', radius: 6378137 })
    )
    var hektar = Math.round((area / 10000) * 10) / 10

    return hektar + 'ha'
  }
}
